import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Terroir-styled toast (zastąpił legacy Tailwind żeby pasował do redesignu).
// API providera bez zmian: success/error/warning/info.
const TONE = {
  success: { fg: 'var(--t-text)', bd: 'rgba(168, 196, 158, 0.4)', bg: 'rgba(168, 196, 158, 0.10)', accent: 'var(--t-sage)',  icon: '✓' },
  error:   { fg: 'var(--t-text)', bd: 'rgba(192, 106, 74, 0.5)',  bg: 'rgba(192, 106, 74, 0.12)',  accent: 'var(--t-rust)',  icon: '✕' },
  warning: { fg: 'var(--t-text)', bd: 'rgba(220, 180, 100, 0.4)', bg: 'rgba(220, 180, 100, 0.10)', accent: 'var(--t-amber)', icon: '⚠' },
  info:    { fg: 'var(--t-text)', bd: 'var(--t-line-2)',          bg: 'var(--t-bg-elev)',          accent: 'var(--t-text-dim)', icon: 'ⓘ' },
};

const Toast = ({ toast, onClose }) => {
  const t = TONE[toast.type] || TONE.info;
  return (
    <div
      role="alert"
      style={{
        pointerEvents: 'auto',
        minWidth: 220,
        maxWidth: '100%',
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderLeft: `3px solid ${t.accent}`,
        borderRadius: 12,
        padding: '12px 14px',
        color: t.fg,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14,
        lineHeight: 1.4,
        letterSpacing: '-.01em',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        animation: 't-toast-in 220ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <span style={{
        flexShrink: 0,
        width: 22, height: 22, borderRadius: '50%',
        background: t.accent,
        color: 'var(--t-bg)',
        display: 'grid', placeItems: 'center',
        fontSize: 13, fontWeight: 600, lineHeight: 1,
      }}>{t.icon}</span>
      <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {toast.title && (
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{toast.title}</div>
        )}
        <div style={{ opacity: toast.title ? 0.9 : 1 }}>{toast.message}</div>
      </div>
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        aria-label="Zamknij"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--t-text-mute)',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
          marginTop: 1,
        }}
      >×</button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((options) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      type: options.type || 'info',
      message: options.message,
      title: options.title,
      duration: options.duration ?? 5000,
    };

    setToasts((prev) => [...prev, toast]);

    // Auto-remove after duration
    if (toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  }, [removeToast]);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return addToast({ ...options, type: 'success', message });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast({ ...options, type: 'error', message });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast({ ...options, type: 'warning', message });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast({ ...options, type: 'info', message });
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  // Eksponuj API na window żeby imperatywny notify() z redesign/lib/notify.js
  // mógł korzystać bez hooka (zamiana 13 plików × window.alert na toast).
  useEffect(() => {
    window.__terroirToast = { success, error, warning, info };
    return () => { delete window.__terroirToast; };
  }, [success, error, warning, info]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container — Terroir styled, prawy dolny róg z safe-area */}
      <style>{`
        @keyframes t-toast-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          right: 'max(16px, env(safe-area-inset-right, 16px))',
          bottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          zIndex: 9500,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'flex-end',
          maxWidth: 'min(420px, calc(100vw - 32px))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <div key={toast.id} style={{ pointerEvents: 'auto', width: '100%' }}>
            <Toast toast={toast} onClose={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
